import { UserRoundIcon } from "lucide-react";

import { RichTextDisplay } from "@/components/RichTextDisplay";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  agenticScoreBadgeClassNames,
  agenticScoreBucketConfig,
  agenticScoreIconClassNames,
  agenticScoreOverrideBadgeClassNames,
  agenticScoreOverrideIconClassNames,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import { AgenticScoreBucket } from "@/types";

// Small corner badge marking a human-overridden rating (agentic ratings show nothing).
export const HumanOverrideBadge = ({
  className,
  variant,
}: {
  className: string;
  variant: "sm" | "xs";
}) => {
  return (
    <span
      className={cn(
        "absolute flex items-center justify-center rounded-full ring-2 ring-white",
        agenticScoreOverrideBadgeClassNames[variant],
        className,
      )}
    >
      <UserRoundIcon
        className={cn("shrink-0", agenticScoreOverrideIconClassNames[variant])}
      />
    </span>
  );
};

const ChipBadge = ({
  bucket,
  classNameOverride,
  isHumanRated,
  variant,
}: {
  bucket: AgenticScoreBucket;
  classNameOverride?: string;
  isHumanRated: boolean;
  variant: "sm" | "xs";
}) => {
  const { Icon, label, className } = agenticScoreBucketConfig[bucket];
  return (
    <Badge
      className={cn(
        "relative border-transparent font-semibold",
        agenticScoreBadgeClassNames[variant],
        className,
        classNameOverride,
      )}
    >
      {isHumanRated && (
        <HumanOverrideBadge className={className} variant={variant} />
      )}
      <Icon className={agenticScoreIconClassNames[variant]} />
      {label}
    </Badge>
  );
};

// Read-only agentic rating chip. Human-overridden ratings show a corner badge and
// reveal their justification on hover; AI ratings do too when one exists.
export const AgenticScoreChip = ({
  bucket,
  className,
  isHumanRated = false,
  justification,
  variant = "xs",
}: {
  bucket: AgenticScoreBucket | null;
  className?: string;
  isHumanRated?: boolean;
  justification?: string | null;
  variant?: "sm" | "xs";
}) => {
  const hasJustificationTooltip = !!justification;

  const render = () => {
    if (!bucket) return null;
    if (!hasJustificationTooltip) {
      return (
        <ChipBadge
          bucket={bucket}
          classNameOverride={className}
          isHumanRated={isHumanRated}
          variant={variant}
        />
      );
    }
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <ChipBadge
                bucket={bucket}
                classNameOverride={className}
                isHumanRated={isHumanRated}
                variant={variant}
              />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-80" side="bottom">
            <RichTextDisplay richText={justification} />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return render();
};
